import React, { useContext, useEffect, useState } from "react";
import { Button, Divider, Input, Modal, Space, Spin, Tag, Typography } from "antd";
import { TbBrandWechat } from "react-icons/tb";
import { FaPhoneAlt } from "react-icons/fa";
import { IoPersonOutline } from "react-icons/io5";
import { RiDeleteBin6Line } from "react-icons/ri";
import { RightOutlined } from "@ant-design/icons";
import { AuthContext } from "../../context/AuthContext";
import { subscribeFleetDriver, updateFleetDriverPlates } from "../../services/fleetDriverService";
import { canAccessDrivers } from "../../auth/fleetRoles";
import { useTranslation } from "react-i18next";
import "./DriverDetailModal.css";
import ImageGallery from "../imageGallery/imageGallery";
import { isColorDark } from "../sideBar/sideBar";
import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useGlobalToast } from "../../context/ToastProvider";
import { formatLastSeenDateTime, getDriverPresence } from "../../utils/driverPresence";
import MapWithLayers from "../map/MapWithLayer";

const languageInfoByPrefix = {
  es: { label: "es-ES", flag: "🇪🇸" },
  ca: { label: "ca-ES", flag: "CA" },
  en: { label: "en-US", flag: "🇬🇧" },
  de: { label: "de-DE", flag: "🇩🇪" },
  fr: { label: "fr-FR", flag: "🇫🇷" },
  ru: { label: "ru-RU", flag: "🇷🇺" },
  pl: { label: "pl-PL", flag: "🇵🇱" },
};
const tagColors = [
  "magenta",
  "red",
  "volcano",
  "orange",
  "gold",
  "lime",
  "green",
  "cyan",
  "blue",
  "geekblue",
  "purple",
];

const getLanguageInfo = (rawLanguage) => {
  if (!rawLanguage || typeof rawLanguage !== "string") {
    return { label: "N/A", flag: "🌐" };
  }

  const normalized = rawLanguage.replace("_", "-").toLowerCase();
  const prefix = normalized.split("-")[0];
  return languageInfoByPrefix[prefix] || { label: rawLanguage, flag: "🌐" };
};

const getColorForTag = (tag) => {
  let hash = 0;
  for (let index = 0; index < tag.length; index += 1) {
    hash += tag.charCodeAt(index);
  }
  return tagColors[hash % tagColors.length];
};

const DriverDetailModal = ({ isOpen, onClose, driverId, onChatClick, deleteDriver }) => {
  const { getFleetID, fleetRole } = useContext(AuthContext);
  const [editingPlates,setEditingPlates]=useState(false);
  const [savingPlates,setSavingPlates]=useState(false);
  const [plates,setPlates]=useState({truckPlate:"",trailerPlate:""});
  const [expectedPlates,setExpectedPlates]=useState({});
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [driverData, setDriverData] = useState({});
  const [registeredDriverData, setRegisteredDriverData] = useState({});
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [selectedCallPhrase, setSelectedCallPhrase] = useState("");
  const { t } = useTranslation();
  const toast = useGlobalToast();
  const { TextArea } = Input;
  useEffect(()=>{setEditingPlates(false);},[driverId,isOpen]);
  const editPlates=()=>{
    const current={truckPlate:driverData.truckPlate || "",trailerPlate:driverData.trailerPlate || ""};
    setPlates(current);setExpectedPlates(current);setEditingPlates(true);
  };
  const savePlates=async()=>{
    setSavingPlates(true);
    try {
      const updated=await updateFleetDriverPlates(getFleetID(),driverId,plates,expectedPlates);
      setDriverData(prev=>({...prev,...updated}));setEditingPlates(false);
      toast({title:t("driverDetail.platesSaved"),status:"success"});
    } catch(error) {
      const key=error.response?.data?.messageKey;
      toast({error,title:key && key.startsWith("driverDetail.plates")?t(key):t("driverDetail.platesSaveFailed"),status:"error"});
    } finally {setSavingPlates(false);}
  };

  const headerBackground = "var(--chakra-colors-gandolapp-500)";
  const headerTextColor = isColorDark(headerBackground) ? "white" : "black";

  useEffect(() => {
    if (!isOpen) return undefined;
    const fleetId = getFleetID();
    if (!fleetId || !driverId) return undefined;

    setLoading(true);
    const registeredDriverRef = doc(db, "fleets", fleetId, "registered_drivers", driverId);

    const unsubscribeDriver = subscribeFleetDriver(
      fleetId, driverId,
      (data) => {
        setDriverData(data || {});
        setLoading(false);
      },
      (error) => {
        console.error("Error loading driver detail:", error);
        setDriverData({});
        setLoading(false);
      }
    );

    const unsubscribeRegisteredDriver = onSnapshot(
      registeredDriverRef,
      (registeredDriverSnapshot) => {
        const data = registeredDriverSnapshot.exists() ? registeredDriverSnapshot.data() : {};
        setRegisteredDriverData(data);
        setTags(Array.isArray(data.tags) ? data.tags : []);
      },
      (error) => {
        console.error("Error loading driver tags:", error);
        setRegisteredDriverData({});
      }
    );

    return () => {
      unsubscribeDriver();
      unsubscribeRegisteredDriver();
    };
  }, [isOpen, getFleetID, driverId]);

  const createTag = async (newTag) => {
    try {
      const fleetId = getFleetID();
      if (!fleetId || !driverId) return;
      const registeredDriverRef = doc(db, "fleets", fleetId, "registered_drivers", driverId);
      await setDoc(registeredDriverRef, { tags: arrayUnion(newTag) }, { merge: true });
    } catch (error) {
      console.log("Error creating tag:", error);
    }
  };

  const deleteTag = async (tagToRemove) => {
    try {
      const fleetId = getFleetID();
      if (!fleetId || !driverId) return;
      const registeredDriverRef = doc(db, "fleets", fleetId, "registered_drivers", driverId);
      await updateDoc(registeredDriverRef, { tags: arrayRemove(tagToRemove) });
    } catch (error) {
      console.log("Error deleting tag:", error);
    }
  };

  const handleTagInputKeyDown = async (event) => {
    if (event.key === "Enter" && tagInput.trim() !== "") {
      const newTag = tagInput.trim();
      await createTag(newTag);
      setTagInput("");
    }
  };

  const handleRemoveTag = async (tagToRemove) => {
    try {
      await deleteTag(tagToRemove);
    } catch (error) {
      console.log("Error removing tag:", error);
    }
  };

  const {
    name,
    phoneNumber,
    language,
    preferredLanguage,
    currentOrder,
    profilePic,
  } = driverData;

  const languageInfo = getLanguageInfo(preferredLanguage || language);
  const currentMode = (driverData?.currentMode || "WORK").toUpperCase();
  const { isOnline } = getDriverPresence(driverData?.isOnline, driverData?.lastSeen);
  const lastSeenLabel = formatLastSeenDateTime(driverData?.lastSeen);
  const communicationStatus = isOnline
    ? t("driverDetail.onlineStatus", { defaultValue: "Online" })
    : lastSeenLabel
    ? t("driverDetail.lastSeenStatus", {
        defaultValue: "Last seen {{dateTime}}",
        dateTime: lastSeenLabel,
      })
    : t("driverDetail.offlineStatus", { defaultValue: "Offline" });
  const hasOrderAssigned = !!currentOrder;
  const operationalStatus =
    currentMode === "REST"
      ? hasOrderAssigned
        ? "REST (ORDER ASSIGNED)"
        : "REST"
      : hasOrderAssigned
      ? "WORK"
      : "WORK (NO ORDER)";
  const gpsAgeMinutes =
    driverData?.lastPositionISO && !Number.isNaN(new Date(driverData.lastPositionISO).getTime())
      ? Math.max(0, Math.round((Date.now() - new Date(driverData.lastPositionISO).getTime()) / 60000))
      : null;

  const lastPosition = registeredDriverData?.lastPosition || {};
  const latitude = Number(lastPosition?.latitude ?? lastPosition?.lat);
  const longitude = Number(lastPosition?.longitude ?? lastPosition?.lng);
  const hasMapPosition = Number.isFinite(latitude) && Number.isFinite(longitude);

  const handleConfirmRemoveDriver = async () => {
    const precheck = await deleteDriver?.(driverId, { dryRun: true, silent: true });
    if (!precheck?.ok) {
      toast({
        title:
          precheck?.reason ||
          t("driverDetail.removeNotPossibleDescription", {
            defaultValue: "This driver cannot be removed from the fleet right now.",
          }),
        status: "warning",
      });
      return;
    }

    Modal.confirm({
      title: t("driverDetail.confirmRemoveTitle", { defaultValue: "Remove driver?" }),
      content: t("driverDetail.confirmRemoveDescription", {
        defaultValue: "This will unregister the driver from this fleet.",
      }),
      okText: t("driverDetail.confirmRemoveOk", { defaultValue: "Remove" }),
      cancelText: t("driverDetail.confirmRemoveCancel", { defaultValue: "Cancel" }),
      okButtonProps: { danger: true },
      onOk: async () => {
        const removal = await deleteDriver?.(driverId);
        if (removal?.ok) {
          onClose?.();
        }
      },
    });
  };

  const handleCallDriver = () => {
    const translatedPhrases = t("driverDetail.callMindfulPhrases", { returnObjects: true });
    const fallbackPhrases = [
      "Calls are useful, but try chat first when the issue is not urgent.",
      "If the driver does not answer, they may be loading or following route safety steps.",
      "A clear message in Gandolapp can be read later without disrupting active operations.",
      "Short, concrete communication reduces stress and saves time for everyone.",
      "Group repeated questions into one message before calling to avoid interruptions.",
      "Use calls mainly for safety or time-critical exceptions.",
      "When possible, confirm updates through chat so there is a trace for the team.",
      "Drivers may be in low-coverage areas; asynchronous chat helps when signal is unstable.",
      "Fewer calls can improve focus and driving safety.",
      "If this call solves a recurring issue, share feedback so we can automate it.",
    ];
    const phrasePool = Array.isArray(translatedPhrases) && translatedPhrases.length > 0 ? translatedPhrases : fallbackPhrases;
    const randomPhrase = phrasePool[Math.floor(Math.random() * phrasePool.length)];
    setSelectedCallPhrase(randomPhrase);
    setIsCallModalOpen(true);
  };

  const handleCallNow = () => {
    if (phoneNumber) {
      window.location.href = `tel:${phoneNumber}`;
    }
  };

  const handleSubmitCallFeedback = async () => {
    const message = feedbackText.trim();
    if (!message) {
      Modal.warning({
        title: t("driverDetail.callFeedbackValidationTitle", { defaultValue: "Feedback required" }),
        content: t("driverDetail.callFeedbackValidation", {
          defaultValue: "Please write a short feedback message before sending.",
        }),
      });
      return;
    }

    const fleetId = getFleetID();
    if (!fleetId || !driverId) return;

    try {
      setIsSubmittingFeedback(true);
      await addDoc(collection(db, "fleets", fleetId, "driver_call_feedback"), {
        driverId,
        driverName: name || null,
        phoneNumber: phoneNumber || null,
        message,
        communicationStatus,
        operationalStatus,
        currentOrder: currentOrder || null,
        gpsAgeMinutes: gpsAgeMinutes ?? null,
        createdAt: serverTimestamp(),
      });
      setFeedbackText("");
      setIsFeedbackModalOpen(false);
      setIsCallModalOpen(false);
      Modal.success({
        title: t("driverDetail.callFeedbackSuccessTitle", { defaultValue: "Thanks for your feedback" }),
        content: t("driverDetail.callFeedbackSuccess", {
          defaultValue: "Your feedback was sent. We will use it to improve automation and reduce unnecessary calls.",
        }),
      });
    } catch (error) {
      console.error("Error saving call feedback:", error);
      Modal.error({
        title: t("driverDetail.callFeedbackErrorTitle", { defaultValue: "Feedback not sent" }),
        content: t("driverDetail.callFeedbackError", {
          defaultValue: "Something failed while sending feedback. Please try again.",
        }),
      });
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  if (!isOpen) return null;

  return (
    <aside className="driver-detail-sidepanel">
      <div className="header driver-detail-header" style={{ color: headerTextColor, backgroundColor: headerBackground }}>
        <div className="driver-detail-panel-toolbar">
          <Button
            type="default"
            icon={<RightOutlined />}
            onClick={onClose}
            className="driver-detail-toolbar-btn"
          />
          <Space size={10}>
            <Button
              type="default"
              icon={<TbBrandWechat size={16} />}
              className="driver-detail-toolbar-btn driver-detail-chat-btn"
              onClick={() =>
                onChatClick?.({
                  id: driverId,
                  name,
                  isOnline,
                  profilePic,
                  ...driverData,
                })
              }
            />
            <Button
              type="default"
              icon={<FaPhoneAlt size={13} />}
              className="driver-detail-toolbar-btn"
              onClick={handleCallDriver}
            />
            <Button
              type="primary"
              danger
              icon={<RiDeleteBin6Line size={14} />}
              className="driver-detail-toolbar-btn"
              onClick={handleConfirmRemoveDriver}
            />
          </Space>
        </div>

        <div className="header-title driver-detail-header-title">
          <div className="driver-detail-profile-statusWrap">
            {profilePic ? (
              <img
                src={profilePic}
                alt={t("driverDetail.profileImageAlt")}
                className="driver-detail-profile-image"
              />
            ) : (
              <div className="driver-detail-profile-fallback">
                <IoPersonOutline size={44} />
              </div>
            )}
            <span
              className={`driver-detail-profile-statusDot ${isOnline ? "online" : "offline"}`}
              title={communicationStatus}
            />
          </div>
          <div>
            <span className="header-data driver-detail-name-data" style={{ color: headerTextColor }}>
              {loading ? <Spin size="small" /> : name || "N/A"}
            </span>
          </div>
        </div>
        <div className="driver-detail-header-meta">
          <div className="driver-detail-language">
            <span className="driver-detail-language-flag" aria-hidden>
              {languageInfo.flag}
            </span>
            <span style={{ color: headerTextColor }}>{languageInfo.label}</span>
          </div>
          <Typography.Text style={{ color: headerTextColor, fontSize: 12 }}>
            {operationalStatus}
          </Typography.Text>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin />
          <Typography.Text style={{ display: "block", marginTop: 12 }}>
            {t("driverDetail.loading")}
          </Typography.Text>
        </div>
      ) : (
        <div className="driver-detail-scroll">
          <div>
            <Typography.Text strong>{t("driverDetail.platesTitle")}</Typography.Text>
            {editingPlates ? <Space direction="vertical" style={{width:"100%",marginTop:8}}>
              {["truckPlate","trailerPlate"].map(key=><label key={key} style={{display:"block"}}>{t(`driverDetail.${key}`)}<Input maxLength={32} value={plates[key]} disabled={savingPlates} onChange={event=>setPlates(prev=>({...prev,[key]:event.target.value}))} /></label>)}
              <Typography.Text type="secondary">{t("driverDetail.platesScope")}</Typography.Text>
              <Space><Button type="primary" loading={savingPlates} onClick={savePlates}>{t("setup.save")}</Button><Button disabled={savingPlates} onClick={()=>setEditingPlates(false)}>{t("setup.cancel")}</Button></Space>
            </Space> : <div style={{display:"grid",gap:6,marginTop:8}}>
              <Typography.Text>{t("driverDetail.truckPlate")}: {driverData.truckPlate || "—"}</Typography.Text>
              <Typography.Text>{t("driverDetail.trailerPlate")}: {driverData.trailerPlate || "—"}</Typography.Text>
              {canAccessDrivers(fleetRole) && <Button onClick={editPlates}>{t("driverDetail.platesEdit")}</Button>}
            </div>}
          </div>
          {hasMapPosition ? (
            <div style={{ borderRadius: 8, overflow: "hidden" }}>
              <MapWithLayers
                latitude={latitude}
                longitude={longitude}
                height={220}
                layers={[
                  {
                    id: "driver-position",
                    type: "marker",
                    markerKind: "driver",
                    data: [
                      {
                        latitude,
                        longitude,
                        isOnline,
                        hasActiveOrder: hasOrderAssigned,
                      },
                    ],
                  },
                ]}
              />
            </div>
          ) : null}

          <div>
            <Typography.Text strong className="driver-detail-section-title">
              {t("driverDetail.tags")}
            </Typography.Text>
            <Input
              placeholder={t("driverDetail.addTags")}
              value={tagInput}
              onChange={(event) => setTagInput(event.target.value)}
              onKeyDown={handleTagInputKeyDown}
              style={{ marginTop: 10 }}
            />
            <div className="driver-detail-tag-cloud">
              {tags.map((tag, index) => (
                <Tag key={index} color={getColorForTag(tag)} closable onClose={() => handleRemoveTag(tag)}>
                  {tag}
                </Tag>
              ))}
            </div>
          </div>

          <div>
            <Divider style={{ margin: "16px 0" }} />

            <ImageGallery
              type="driver"
              fleetId={getFleetID()}
              userId={driverId}
              titleClassName="driver-detail-section-title"
            />
          </div>
        </div>
      )}
      <Modal
        open={isCallModalOpen}
        title={t("driverDetail.callDriverTitle", { defaultValue: "Call driver" })}
        onCancel={() => setIsCallModalOpen(false)}
        footer={[
          <Button key="feedback" onClick={() => setIsFeedbackModalOpen(true)}>
            {t("driverDetail.callFeedbackButton", { defaultValue: "Send feedback" })}
          </Button>,
          <Button key="close" onClick={() => setIsCallModalOpen(false)}>
            {t("driverDetail.close", { defaultValue: "Close" })}
          </Button>,
          <Button key="call" type="primary" onClick={handleCallNow} disabled={!phoneNumber}>
            {t("driverDetail.callNow", { defaultValue: "Call now" })}
          </Button>,
        ]}
      >
        <div style={{ display: "grid", gap: 10 }}>
          <div>
            <Typography.Text strong>{t("driverDetail.phone", { defaultValue: "Phone" })}: </Typography.Text>
            <Typography.Text>{phoneNumber || "N/A"}</Typography.Text>
          </div>
          <Typography.Text type="secondary">{selectedCallPhrase}</Typography.Text>
          <div style={{ fontSize: 12, color: "#111827" }}>
            {`${t("driverDetail.callStatus", { defaultValue: "Status" })}: ${communicationStatus} | ${
              t("driverDetail.callAssignedOrder", { defaultValue: "Assigned order" })
            }: ${currentOrder || t("driverDetail.none", { defaultValue: "None" })} | ${
              t("driverDetail.callGpsFreshness", { defaultValue: "GPS freshness" })
            }: ${gpsAgeMinutes == null ? "N/A" : `${gpsAgeMinutes} min`}`}
          </div>
        </div>
      </Modal>
      <Modal
        open={isFeedbackModalOpen}
        title={t("driverDetail.callFeedbackTitle", { defaultValue: "Call feedback" })}
        onCancel={() => setIsFeedbackModalOpen(false)}
        onOk={handleSubmitCallFeedback}
        okText={t("driverDetail.callFeedbackSubmit", { defaultValue: "Send feedback" })}
        confirmLoading={isSubmittingFeedback}
      >
        <Space direction="vertical" style={{ width: "100%" }} size={8}>
          <Typography.Text type="secondary">
            {t("driverDetail.callFeedbackDescription", {
              defaultValue:
                "Tell us what this call solved and how Gandolapp could handle it automatically next time.",
            })}
          </Typography.Text>
          <TextArea
            rows={4}
            value={feedbackText}
            onChange={(event) => setFeedbackText(event.target.value)}
            placeholder={t("driverDetail.callFeedbackPlaceholder", {
              defaultValue: "Example: I called to ask for ETA. It could be solved with proactive ETA alerts.",
            })}
          />
        </Space>
      </Modal>
    </aside>
  );
};

export default DriverDetailModal;
